/* ================================================================
   3D Viewport - React Three Fiber based 3D scene.

   Optimizations:
   - BoardMesh is memoized with a custom comparator (shallow node
     compare) so unchanged meshes never re-run React render.
   - Edges geometry is constructed once per (w,h,d) via useMemo
     and disposed on unmount.
   - Raycast iterates an Array snapshot of the registry to avoid
     allocation per click.
   - Viewport listens to `designer:view` custom events for ribbon
     view actions (top/front/side/iso/reset/grid).
   ================================================================ */

import React, {
  useRef, useCallback, useMemo, useEffect, createContext, useContext, useState,
} from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, GizmoHelper, GizmoViewport, Grid } from '@react-three/drei';
import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { useEditorStore } from '../state/editorStore';
import type { RenderNode, SideId } from '../core/model/types';
import {
  getBaseColor,
  SCALE_FACTOR, calculateCenterOffset,
} from '../core/renderTree';
import { resolveMaterial } from '../core/materials';
import {
  getCachedTexture, loadTexture,
  getCachedNormalMap, loadNormalMap, hashSeed,
} from '../core/textures';

type MeshRegistry = Map<string, THREE.Mesh>;
const MeshRegistryContext = createContext<MeshRegistry>(new Map());

function getPlanarDimensions(
  sideId: SideId,
  node?: { geometry: { width: number; height: number; depth: number } } | null,
) {
  if (!node) return { length: 0, width: 0 };
  let a: number, b: number;
  switch (sideId) {
    case 'pedestal':
    case 'topCap':
      a = node.geometry.width;
      b = node.geometry.depth;
      break;
    case 'sideFront':
    case 'sideBack':
      a = node.geometry.width;
      b = node.geometry.height;
      break;
    case 'endLeft':
    case 'endRight':
      a = node.geometry.depth;
      b = node.geometry.height;
      break;
  }
  return a >= b ? { length: a, width: b } : { length: b, width: a };
}

interface BoardMeshProps {
  node: RenderNode;
  isSelected: boolean;
  isHovered: boolean;
  centerOffset: [number, number, number];
  explodedOffset: [number, number, number];
}

function boardMeshPropsEqual(prev: BoardMeshProps, next: BoardMeshProps): boolean {
  if (prev.isSelected !== next.isSelected) return false;
  if (prev.isHovered !== next.isHovered) return false;
  if (prev.centerOffset[0] !== next.centerOffset[0]
      || prev.centerOffset[1] !== next.centerOffset[1]
      || prev.centerOffset[2] !== next.centerOffset[2]) return false;
  if (prev.explodedOffset[0] !== next.explodedOffset[0]
      || prev.explodedOffset[1] !== next.explodedOffset[1]
      || prev.explodedOffset[2] !== next.explodedOffset[2]) return false;
  const a = prev.node;
  const b = next.node;
  if (a === b) return true;
  if (a.meshId !== b.meshId) return false;
  if (a.material !== b.material) return false;
  if (a.materialcolor !== b.materialcolor) return false;
  if (a.geometry.width !== b.geometry.width
      || a.geometry.height !== b.geometry.height
      || a.geometry.depth !== b.geometry.depth) return false;
  if (a.position.x !== b.position.x
      || a.position.y !== b.position.y
      || a.position.z !== b.position.z) return false;
  return true;
}

function useNodeTexture(node: RenderNode): THREE.Texture | null {
  const { gl } = useThree();
  const project = useEditorStore(s => s.project);
  const material = useMemo(
    () => resolveMaterial(project, node.material),
    [project, node.material],
  );
  const url = material.textureUrl;
  const tileMm = material.textureTileMm ?? 400;
  const { width, height, depth } = node.geometry;

  const [texture, setTexture] = useState<THREE.Texture | null>(() => {
    if (!url) return null;
    const cached = getCachedTexture(url);
    return cached ? cloneAndConfigure(cached, width, height, depth, tileMm, gl.capabilities.getMaxAnisotropy()) : null;
  });

  useEffect(() => {
    if (!url) { setTexture(null); return; }
    let cancelled = false;
    let localTex: THREE.Texture | null = null;

    const apply = (shared: THREE.Texture) => {
      if (cancelled) return;
      localTex = cloneAndConfigure(shared, width, height, depth, tileMm, gl.capabilities.getMaxAnisotropy());
      setTexture(localTex);
    };

    const cached = getCachedTexture(url);
    if (cached) apply(cached);
    else loadTexture(url).then(apply).catch(() => {});

    return () => {
      cancelled = true;
      localTex?.dispose();
    };
  }, [depth, gl, height, tileMm, url, width]);

  return texture;
}

function cloneAndConfigure(
  shared: THREE.Texture,
  wMm: number, hMm: number, dMm: number, tileMm: number,
  anisotropy: number,
): THREE.Texture {
  const tex = shared.clone();
  tex.needsUpdate = true;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = anisotropy;
  // Stretch one texture across the whole board body instead of tiling it.
  tex.repeat.set(1, 1);
  tex.offset.set(0, 0);
  return tex;
}

interface FaceAsset {
  map: THREE.Texture;
  normal: THREE.Texture | null;
}
type FaceAssetSet = Array<FaceAsset | null>;

function useNodeFaceTextures(node: RenderNode): FaceAssetSet | null {
  const { gl } = useThree();
  const project = useEditorStore((s) => s.project);
  const material = useMemo(
    () => resolveMaterial(project, node.material),
    [project, node.material],
  );
  const url = material.textureUrl;
  const tileMm = material.textureTileMm ?? 400;
  const { width, height, depth } = node.geometry;
  const anisotropy = gl.capabilities.getMaxAnisotropy();
  const seed = useMemo(() => hashSeed(node.meshId), [node.meshId]);

  const [assets, setAssets] = useState<FaceAssetSet | null>(() => {
    if (!url) return null;
    const diffuse = getCachedTexture(url);
    if (!diffuse) return null;
    const normal = getCachedNormalMap(url);
    return cloneFaceTextures(diffuse, normal, width, height, depth, tileMm, anisotropy, seed);
  });

  useEffect(() => {
    if (!url) {
      setAssets(null);
      return;
    }
    let cancelled = false;
    let localAssets: FaceAssetSet | null = null;

    const applyFrom = (diffuse: THREE.Texture) => {
      if (cancelled) return;
      localAssets?.forEach(item => {
        if (!item) return;
        item.map.dispose();
        item.normal?.dispose();
      });
      const normal = getCachedNormalMap(url);
      localAssets = cloneFaceTextures(diffuse, normal, width, height, depth, tileMm, anisotropy, seed);
      setAssets(localAssets);
    };

    const cachedDiffuse = getCachedTexture(url);
    if (cachedDiffuse) {
      applyFrom(cachedDiffuse);
      // Normal map may still be baking — subscribe through a second promise.
      if (!getCachedNormalMap(url)) {
        void loadNormalMap(url).then(() => {
          if (!cancelled) applyFrom(cachedDiffuse);
        });
      }
    } else {
      loadTexture(url).then(diffuse => {
        applyFrom(diffuse);
        void loadNormalMap(url).then(() => {
          if (!cancelled) applyFrom(diffuse);
        });
      }).catch(() => {});
    }

    return () => {
      cancelled = true;
      localAssets?.forEach(item => {
        if (!item) return;
        item.map.dispose();
        item.normal?.dispose();
      });
    };
  }, [anisotropy, depth, height, seed, tileMm, url, width]);

  return assets;
}

function cloneFaceTextures(
  diffuse: THREE.Texture,
  normal: THREE.Texture | null,
  widthMm: number,
  heightMm: number,
  depthMm: number,
  tileMm: number,
  anisotropy: number,
  seed: number,
): FaceAssetSet {
  const majorAxis =
    widthMm >= heightMm && widthMm >= depthMm
      ? 'x'
      : heightMm >= depthMm
        ? 'y'
        : 'z';

  // BoxGeometry material order: +X, -X, +Y, -Y, +Z, -Z
  const faceConfigs = [
    { faceWidth: depthMm, faceHeight: heightMm, normalAxis: 'x', rotate: majorAxis === 'y' },
    { faceWidth: depthMm, faceHeight: heightMm, normalAxis: 'x', rotate: majorAxis === 'y' },
    { faceWidth: widthMm, faceHeight: depthMm, normalAxis: 'y', rotate: majorAxis === 'z' },
    { faceWidth: widthMm, faceHeight: depthMm, normalAxis: 'y', rotate: majorAxis === 'z' },
    { faceWidth: widthMm, faceHeight: heightMm, normalAxis: 'z', rotate: majorAxis === 'y' },
    { faceWidth: widthMm, faceHeight: heightMm, normalAxis: 'z', rotate: majorAxis === 'y' },
  ];

  // Per-plank offsets (deterministic). Shift each face by a different
  // amount so the six sides of the same plank aren't perfectly aligned.
  const offsetU = seed;
  const offsetV = (seed * 0.6180339) % 1; // golden ratio spread

  const configure = (tex: THREE.Texture, config: typeof faceConfigs[number], isNormal: boolean): THREE.Texture => {
    tex.needsUpdate = true;
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.colorSpace = isNormal ? THREE.NoColorSpace : THREE.SRGBColorSpace;
    tex.anisotropy = anisotropy;
    // Match the legacy look: one board face uses one stretched texture patch.
    tex.repeat.set(1, 1);
    tex.offset.set(offsetU * 0.08, offsetV * 0.08);
    if (config.rotate) {
      tex.center.set(0.5, 0.5);
      tex.rotation = Math.PI / 2;
    }
    return tex;
  };

  return faceConfigs.map(config => {
    const mapClone = configure(diffuse.clone(), config, false);
    const normalClone = normal ? configure(normal.clone(), config, true) : null;
    return { map: mapClone, normal: normalClone };
  });
}

const BoardMesh = React.memo(function BoardMesh({
  node, isSelected, isHovered, centerOffset, explodedOffset,
}: BoardMeshProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const registry = useContext(MeshRegistryContext);

  const { width, height, depth } = node.geometry;
  const w = width * SCALE_FACTOR;
  const h = height * SCALE_FACTOR;
  const d = depth * SCALE_FACTOR;

  const px = node.position.x * SCALE_FACTOR + centerOffset[0] + explodedOffset[0];
  const py = node.position.y * SCALE_FACTOR + centerOffset[1] + explodedOffset[1];
  const pz = node.position.z * SCALE_FACTOR + centerOffset[2] + explodedOffset[2];

  // 板边描边（与 PDF 导出 threeRenderer.ts 同款：LineSegments2 + LineMaterial，纯黑粗线）
  // WebGL 的 lineBasicMaterial 在主流浏览器上 linewidth 被强制为 1px，必须用 LineSegments2 才能加粗
  const lineGeometry = useMemo(() => {
    const edges = new THREE.EdgesGeometry(new THREE.BoxGeometry(w, h, d));
    const positions = edges.attributes.position.array as Float32Array;
    const lg = new LineSegmentsGeometry();
    lg.setPositions(Array.from(positions));
    edges.dispose();
    return lg;
  }, [w, h, d]);
  useEffect(() => () => lineGeometry.dispose(), [lineGeometry]);

  const { size: canvasSize } = useThree();
  const lineMaterial = useMemo(() => {
    return new LineMaterial({
      color: 0x000000,
      linewidth: 1.4,  // 像素单位，live 编辑器细一点不挡视线
      transparent: false,
      depthTest: true,
      resolution: new THREE.Vector2(canvasSize.width, canvasSize.height),
    });
    // resolution 在另一个 effect 里跟随 canvas 尺寸更新，不放进依赖
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    lineMaterial.resolution.set(canvasSize.width, canvasSize.height);
  }, [canvasSize.width, canvasSize.height, lineMaterial]);
  useEffect(() => () => lineMaterial.dispose(), [lineMaterial]);

  const lineSegments = useMemo(() => {
    const ls = new LineSegments2(lineGeometry, lineMaterial);
    ls.scale.set(1.001, 1.001, 1.001);  // 略大一点避免与板面 z-fighting
    ls.computeLineDistances();
    return ls;
  }, [lineGeometry, lineMaterial]);

  // 选中/悬停时改色 + 加粗（PDF 是静态导出，不需要这些状态；live 编辑器需要视觉反馈）
  useEffect(() => {
    if (isSelected) {
      lineMaterial.color.set(0xff6a00);
      lineMaterial.linewidth = 2.4;
    } else if (isHovered) {
      lineMaterial.color.set(0xff8c00);
      lineMaterial.linewidth = 1.8;
    } else {
      lineMaterial.color.set(0x000000);
      lineMaterial.linewidth = 0.8;
    }
  }, [isSelected, isHovered, lineMaterial]);

  const texture = useNodeTexture(node);
  const faceAssets = useNodeFaceTextures(node);
  useEffect(() => () => { texture?.dispose(); }, [texture]);

  useEffect(() => {
    const mesh = meshRef.current;
    if (mesh) {
      registry.set(node.meshId, mesh);
      return () => { registry.delete(node.meshId); };
    }
  }, [node.meshId, registry]);

  const baseColor = getBaseColor(node);

  const materials = useMemo(() => {
    const majorAxis = width >= height && width >= depth
      ? 'x'
      : height >= depth
        ? 'y'
        : 'z';

    return new Array(6).fill(null).map((_, index) => {
      const normalAxis = index < 2 ? 'x' : index < 4 ? 'y' : 'z';
      const isEndFace = normalAxis === majorAxis;

      const resolvedColor = isEndFace
        ? shadeColor(baseColor, 0.84)
        : shadeColor(baseColor, 1.02);

      const asset = isSelected ? null : faceAssets?.[index] ?? null;
      const map = asset?.map ?? (isSelected ? null : texture ?? null);
      const normalMap = isSelected ? null : asset?.normal ?? null;
      const ns = isEndFace ? 0.12 : 0.34;

      return new THREE.MeshPhysicalMaterial({
        color: resolvedColor,
        map,
        normalMap,
        normalScale: new THREE.Vector2(ns, ns),
        roughness: isEndFace ? 0.92 : 0.83,
        metalness: 0,
        clearcoat: 0,
        clearcoatRoughness: 1,
        sheen: 0.02,
        sheenRoughness: 0.98,
        emissive: new THREE.Color(isSelected ? '#ff6600' : isHovered ? '#ff9940' : '#000000'),
        emissiveIntensity: isSelected ? 0.45 : isHovered ? 0.2 : 0,
        envMapIntensity: isSelected ? 0.1 : 0.28,
        transparent: false,
        opacity: 1,
      });
    });
  }, [baseColor, depth, faceAssets, height, isHovered, isSelected, texture, width]);

  useEffect(() => () => {
    materials.forEach((item) => item.dispose());
  }, [materials]);

  return (
    <group position={[px, py, pz]}>
      <mesh ref={meshRef} castShadow receiveShadow>
        <boxGeometry args={[w, h, d]} />
        {materials.map((material, index) => (
          <primitive key={index} object={material} attach={`material-${index}`} />
        ))}
      </mesh>
      <primitive object={lineSegments} />
    </group>
  );
}, boardMeshPropsEqual);

function shadeColor(input: string, factor: number): string {
  const color = new THREE.Color(input);
  color.multiplyScalar(factor);
  return `#${color.getHexString()}`;
}

function RaycastHandler({ registry }: { registry: MeshRegistry }) {
  const { camera, gl } = useThree();

  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const mouse = useMemo(() => new THREE.Vector2(), []);
  const pointerDownPos = useRef<{ x: number; y: number } | null>(null);
  const meshArrayRef = useRef<THREE.Mesh[]>([]);

  const selectMesh = useEditorStore(s => s.selectMesh);
  const deselectAll = useEditorStore(s => s.deselectAll);
  const setHoveredMesh = useEditorStore(s => s.setHoveredMesh);
  const showContextMenu = useEditorStore(s => s.showContextMenu);

  useEffect(() => {
    const canvas = gl.domElement;

    function refreshMeshArray() {
      meshArrayRef.current = Array.from(registry.values());
    }

    function findHitMeshId(e: MouseEvent): string | null {
      const rect = canvas.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);

      if (meshArrayRef.current.length !== registry.size) refreshMeshArray();
      if (meshArrayRef.current.length === 0) return null;

      const intersects = raycaster.intersectObjects(meshArrayRef.current, false);
      if (intersects.length === 0) return null;

      const hitMesh = intersects[0].object;
      for (const [id, mesh] of registry) {
        if (mesh === hitMesh) return id;
      }
      return null;
    }

    function onPointerDown(e: PointerEvent) {
      if (e.button <= 1) {
        pointerDownPos.current = { x: e.clientX, y: e.clientY };
      }
    }

    function onPointerUp(e: PointerEvent) {
      if (!pointerDownPos.current || e.button !== 0) {
        pointerDownPos.current = null;
        return;
      }
      const dx = Math.abs(e.clientX - pointerDownPos.current.x);
      const dy = Math.abs(e.clientY - pointerDownPos.current.y);
      pointerDownPos.current = null;
      if (dx > 5 || dy > 5) return;

      const meshId = findHitMeshId(e);
      if (meshId) {
        selectMesh(meshId, e.ctrlKey || e.metaKey);
      } else {
        deselectAll();
      }
    }

    function onContextMenu(e: MouseEvent) {
      e.preventDefault();
      const meshId = findHitMeshId(e);
      if (meshId) {
        const state = useEditorStore.getState();
        if (!state.selectedMeshIds[meshId]) {
          selectMesh(meshId, false);
        }
        const root = canvas.closest('[data-designer-context-root="true"]') as HTMLElement | null;
        const rect = (root ?? canvas).getBoundingClientRect();
        showContextMenu(e.clientX - rect.left, e.clientY - rect.top);
      }
    }

    let moveFrame = 0;
    let pendingEvent: PointerEvent | null = null;
    function onPointerMove(e: PointerEvent) {
      pendingEvent = e;
      if (moveFrame) return;
      moveFrame = requestAnimationFrame(() => {
        moveFrame = 0;
        if (!pendingEvent) return;
        const meshId = findHitMeshId(pendingEvent);
        setHoveredMesh(meshId);
      });
    }

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('contextmenu', onContextMenu);
    canvas.addEventListener('pointermove', onPointerMove);

    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('contextmenu', onContextMenu);
      canvas.removeEventListener('pointermove', onPointerMove);
      if (moveFrame) cancelAnimationFrame(moveFrame);
    };
  }, [camera, gl, raycaster, mouse, registry, selectMesh, deselectAll, setHoveredMesh, showContextMenu]);

  return null;
}

function CameraController({
  showGrid, setShowGrid, exploded, setExploded,
}: {
  showGrid: boolean;
  setShowGrid: (v: boolean) => void;
  exploded: boolean;
  setExploded: (v: boolean) => void;
}) {
  const { camera } = useThree();
  const controls = useRef<any>(null);

  useEffect(() => {
    function onView(e: Event) {
      const kind = (e as CustomEvent<string>).detail;
      if (!controls.current) return;
      const c = controls.current;
      if (kind === 'grid') {
        setShowGrid(!showGrid);
        return;
      }
      if (kind === 'explode') {
        setExploded(!exploded);
        return;
      }
      c.reset?.();
      switch (kind) {
        case 'top':
          camera.position.set(0, 3, 0.0001);
          break;
        case 'front':
          camera.position.set(0, 0.5, 3);
          break;
        case 'side':
          camera.position.set(3, 0.5, 0);
          break;
        case 'iso':
        case 'reset':
          camera.position.set(2, 1.5, 2);
          break;
      }
      camera.lookAt(0, 0, 0);
      c.update?.();
    }
    window.addEventListener('designer:view', onView);
    return () => window.removeEventListener('designer:view', onView);
  }, [camera, exploded, setExploded, showGrid, setShowGrid]);

  return <OrbitControls ref={controls} makeDefault enableDamping dampingFactor={0.1} />;
}

function SceneContent({
  registry, showGrid, setShowGrid, exploded, setExploded,
}: {
  registry: MeshRegistry;
  showGrid: boolean;
  setShowGrid: (v: boolean) => void;
  exploded: boolean;
  setExploded: (v: boolean) => void;
}) {
  const renderNodes = useEditorStore(s => s.renderNodes);
  const selectedMeshIds = useEditorStore(s => s.selectedMeshIds);
  const hoveredMeshId = useEditorStore(s => s.hoveredMeshId);
  const project = useEditorStore(s => s.project);

  const centerOffset = useMemo(() =>
    calculateCenterOffset(
      project.outerSize.length,
      project.outerSize.width,
      project.outerSize.height,
    ), [project.outerSize.length, project.outerSize.width, project.outerSize.height]);

  const explodedOffsets = useMemo(() => {
    const offsets = new Map<string, [number, number, number]>();
    if (!exploded) return offsets;

    const maxDim = Math.max(
      project.outerSize.length,
      project.outerSize.width,
      project.outerSize.height,
    );
    const stepMm = Math.max(18, 0.03 * maxDim);
    const capMm = 4 * stepMm;
    const sideMaxLayer = new Map<RenderNode['sideId'], number>();
    for (const node of renderNodes) {
      const prev = sideMaxLayer.get(node.sideId) ?? -1;
      if (node.layerIndex > prev) sideMaxLayer.set(node.sideId, node.layerIndex);
    }

    for (const node of renderNodes) {
      let direction: [number, number, number] = [0, 0, 0];
      let baseMm = capMm;
      const maxLayerIndex = sideMaxLayer.get(node.sideId) ?? node.layerIndex;

      switch (node.sideId) {
        case 'pedestal':
          baseMm = Math.min(project.outerSize.height / 2, capMm);
          direction = [0, -1, 0];
          break;
        case 'topCap':
          baseMm = Math.min(project.outerSize.height / 2, capMm);
          direction = [0, 1, 0];
          break;
        case 'sideFront':
          baseMm = Math.min(project.outerSize.width / 2, capMm);
          direction = [0, 0, -1];
          break;
        case 'sideBack':
          baseMm = Math.min(project.outerSize.width / 2, capMm);
          direction = [0, 0, 1];
          break;
        case 'endLeft':
          baseMm = Math.min(project.outerSize.length / 2, capMm);
          direction = [-1, 0, 0];
          break;
        case 'endRight':
          baseMm = Math.min(project.outerSize.length / 2, capMm);
          direction = [1, 0, 0];
          break;
      }

      const totalMm = baseMm + (stepMm * (maxLayerIndex - node.layerIndex));
      offsets.set(node.meshId, [
        direction[0] * totalMm * SCALE_FACTOR,
        direction[1] * totalMm * SCALE_FACTOR,
        direction[2] * totalMm * SCALE_FACTOR,
      ]);
    }

    return offsets;
  }, [
    exploded,
    project.outerSize.height,
    project.outerSize.length,
    project.outerSize.width,
    renderNodes,
  ]);

  return (
    <MeshRegistryContext.Provider value={registry}>
      <color attach="background" args={['#ffffff']} />
      <PhotoLighting />
      <SceneEnvironment />

      {showGrid ? (
        <Grid
          args={[10, 10]}
          cellSize={0.1}
          cellThickness={0.5}
          cellColor="#cbd5e1"
          sectionSize={0.5}
          sectionThickness={1}
          sectionColor="#94a3b8"
          fadeDistance={12}
          fadeStrength={1.2}
          position={[0, centerOffset[1], 0]}
        />
      ) : null}

      {renderNodes.map(node => (
        <BoardMesh
          key={node.meshId}
          node={node}
          isSelected={!!selectedMeshIds[node.meshId]}
          isHovered={hoveredMeshId === node.meshId}
          centerOffset={centerOffset}
          explodedOffset={explodedOffsets.get(node.meshId) ?? [0, 0, 0]}
        />
      ))}

      <RaycastHandler registry={registry} />
      <CameraController
        showGrid={showGrid}
        setShowGrid={setShowGrid}
        exploded={exploded}
        setExploded={setExploded}
      />

      <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
        <GizmoViewport labelColor="white" axisHeadScale={0.8} />
      </GizmoHelper>

    </MeshRegistryContext.Provider>
  );
}

function SceneEnvironment() {
  const { gl, scene } = useThree();

  useEffect(() => {
    const pmrem = new THREE.PMREMGenerator(gl);
    pmrem.compileEquirectangularShader();
    const environment = new RoomEnvironment();
    const renderTarget = pmrem.fromScene(environment, 0.02);
    const previousEnv = scene.environment;
    const previousIntensity = (scene as any).environmentIntensity ?? 1;
    scene.environment = renderTarget.texture;
    (scene as any).environmentIntensity = 0.7;
    return () => {
      scene.environment = previousEnv;
      (scene as any).environmentIntensity = previousIntensity;
      renderTarget.dispose();
      pmrem.dispose();
    };
  }, [gl, scene]);

  return null;
}


function PhotoLighting() {
  return (
    <>
      <ambientLight intensity={0.85} color="#f8f1e5" />
      <directionalLight
        position={[5, 8, 4]}
        intensity={1.75}
        color="#fff4dc"
        castShadow
        shadow-mapSize-width={4096}
        shadow-mapSize-height={4096}
        shadow-camera-near={0.1}
        shadow-camera-far={24}
        shadow-camera-left={-5}
        shadow-camera-right={5}
        shadow-camera-top={5}
        shadow-camera-bottom={-5}
        shadow-bias={-0.00025}
        shadow-radius={12}
      />
      <directionalLight position={[-4, 5, -2]} intensity={0.55} color="#d9e4ee" />
      <spotLight
        position={[0, 6.5, 0]}
        angle={0.72}
        penumbra={0.45}
        intensity={0.65}
        color="#fff7ea"
        distance={18}
      />
    </>
  );
}

function SelectionOverlay() {
  const renderNodes = useEditorStore(s => s.renderNodes);
  const selectedMeshIds = useEditorStore(s => s.selectedMeshIds);
  const project = useEditorStore(s => s.project);

  const selectedNodes = useMemo(
    () => renderNodes.filter(node => selectedMeshIds[node.meshId]),
    [renderNodes, selectedMeshIds],
  );

  const info = useMemo(() => {
    if (selectedNodes.length === 0) return null;

    const firstNode = selectedNodes[0];
    const side = project.sides.find(item => item.sideId === firstNode.sideId);
    const layer = side?.layers[firstNode.layerIndex];
    if (!layer) return null;

    const planar = getPlanarDimensions(firstNode.sideId, firstNode);
    const selectedLayerKeys = new Set(selectedNodes.map(node => `${node.sideId}-${node.layerIndex}`));
    const material = resolveMaterial(project, layer.materialKey);

    return {
      layer,
      node: firstNode,
      planar,
      material,
      meshCount: selectedNodes.length,
      layerCount: selectedLayerKeys.size,
    };
  }, [project, selectedNodes]);

  if (!info) {
    return (
      <div className="pointer-events-none absolute right-3 top-3 max-w-[180px] rounded-md bg-[rgba(15,23,42,0.08)] px-2.5 py-2 text-[10px] text-white/45-[2px]">
        <div className="tracking-[0.12em] text-white/52">3D 属性</div>
        <div className="mt-0.5 leading-4">选中板件后显示</div>
      </div>
    );
  }

  const { layer, node, planar, material, meshCount, layerCount } = info;
  const styleLabel = {
    0: '板条',
    1: '整板',
    3: '田字框架',
    4: '木方/垫块',
  }[layer.style] || '未知';

  const sideLabel = {
    pedestal: '底座',
    sideFront: '前面',
    sideBack: '后面',
    endLeft: '左端',
    endRight: '右端',
    topCap: '顶盖',
  }[node.sideId];

  return (
    <div className="pointer-events-none absolute right-3 top-3 w-[190px] rounded-md bg-[rgba(15,23,42,0.09)] px-2.5 py-2 text-[10px] text-white/58-[2px]">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[8px] uppercase tracking-[0.16em] text-white/38">Selection</div>
          <div className="mt-0.5 truncate text-[11px] font-medium text-white/78">{layer.name}</div>
        </div>
        <div className="rounded-full bg-white/6 px-1.5 py-0.5 text-[8px] text-white/42">
          {meshCount > 1 ? `${meshCount} 个构件` : '单个构件'}
        </div>
      </div>

      <div className="mt-1.5 space-y-1 leading-4">
        <OverlayLine label="所在面" value={sideLabel} />
        <OverlayLine label="层级" value={`第 ${node.layerIndex + 1} 层`} />
        <OverlayLine label="类型" value={styleLabel} />
        <OverlayLine label="方向" value={layer.direction === 1 ? '横向' : '纵向'} />
        <OverlayLine label="尺寸" value={`${planar.length || layer.blockBoard.boardLength || 0} × ${planar.width || layer.blockBoard.boardWidth || 0} × ${layer.thickness}`} />
        <OverlayLine label="材质" value={material.name} />
        <div className="flex items-center gap-1.5 pt-0.5 text-[9px] text-white/38">
          <span className="inline-block h-2 w-2 rounded-sm bg-white/55" style={{ background: layer.color }} />
          <span className="truncate">{layer.color}</span>
          {layerCount > 1 ? <span className="ml-auto">跨 {layerCount} 层</span> : null}
        </div>
      </div>
    </div>
  );
}

function OverlayLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <span className="w-[34px] flex-none text-[9px] text-white/32">{label}</span>
      <span className="min-w-0 flex-1 truncate text-[10px] text-white/62">{value}</span>
    </div>
  );
}


export default function Viewport3D() {
  const registry = useMemo<MeshRegistry>(() => new Map(), []);
  const [showGrid, setShowGrid] = useState(false);
  const [exploded, setExploded] = useState(false);

  return (
    <div className="box-designer-viewport relative h-full w-full">
      <Canvas
        shadows
        camera={{ position: [2, 1.5, 2], fov: 50, near: 0.01, far: 100 }}
        gl={{
          antialias: true,
          preserveDrawingBuffer: true,
          outputColorSpace: THREE.SRGBColorSpace,
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 1.18,
        }}
        style={{ background: 'linear-gradient(160deg,#f8fafc 0%,#e2e8f0 100%)' }}
      >
        <SceneContent
          registry={registry}
          showGrid={showGrid}
          setShowGrid={setShowGrid}
          exploded={exploded}
          setExploded={setExploded}
        />
      </Canvas>
      <SelectionOverlay />
    </div>
  );
}
